import { useState } from "react"
import { BRK_ILLEGAL_6502, BRK_ILLEGAL_65C02, BRK_INSTR, getBreakpointString } from "../../../common/breakpoint"
import EditField from "../editfield"
import PullDownMenu from "../pulldownmenu"
import { Droplist } from "../droplist"
import { MEMORY_BANKS, MemoryBankKeys, MemoryBankNames } from "../../../common/memorybanks"
import { opCodeNames, opCodes, opTable } from "../../../common/opcodes"
import Breakpoint_Actions from "./breakpoint_actions"
import CheckBox from "../checkbox"
import Breakpoint_Once from "./breakpoint_once"
import { useTranslation } from "../../../i18n/useTranslation"

const BPEdit_Instruction = (props: {
  breakpoint: Breakpoint,
  setBreakpoint: (bp: Breakpoint) => void,
}) => {
  const { t } = useTranslation()
  const addressModes = [
    t("debug.breakpoint.mode.implied"),
    t("debug.breakpoint.mode.immediate") + " #$FF",
    t("debug.breakpoint.mode.zeroPageRelative"),
    t("debug.breakpoint.mode.zeroPageX") + " $FF,X",
    t("debug.breakpoint.mode.zeroPageY") + " $FF,Y",
    t("debug.breakpoint.mode.absolute") + " $1234",
    t("debug.breakpoint.mode.absoluteX") + " $1234,X",
    t("debug.breakpoint.mode.absoluteY") + " $1234,Y",
    t("debug.breakpoint.mode.indirectX") + " ($FF,X)",
    t("debug.breakpoint.mode.indirectY") + " ($FF),Y",
    t("debug.breakpoint.mode.indirect") + " ($FF)",
  ]
  const [myInit, setMyInit] = useState(false)
  const [instruction, setInstruction] = useState("")
  const [popup, setPopup] = useState<string[]>([])
  const [addressMode, setAddressMode] = useState("")
  const [illegal65C02, setIllegal65C02] = useState(props.breakpoint.address === BRK_ILLEGAL_65C02)
  const [illegal6502, setIllegal6502] = useState(props.breakpoint.address === BRK_ILLEGAL_6502)

  const resetPopup = () => {
    setPopup(opCodeNames)
  }

  if (!myInit) {
    if (props.breakpoint.address >= BRK_INSTR) {
      const opcode = props.breakpoint.address & 0xFF
      setInstruction(opCodes[opcode].name)
      setAddressMode(addressModes[opCodes[opcode].mode])
    }
    resetPopup()
    setMyInit(true)
  }

  const handleInstructionChange = (value: string) => {
    value = value.replace(/[^a-z]/gi, "").slice(0, 3).toUpperCase()
    const newPopup = []
    // Add all possible opcode matches to the popup list.
    if (value.length >= 0) {
      for (let i = 0; i < opCodeNames.length; i++) {
        if (opCodeNames[i].startsWith(value)) {
          newPopup.push(opCodeNames[i])
        }
      }
    }
    // No matches? Clear the current address and do not accept the new value.
    if (newPopup.length === 0) {
      props.setBreakpoint({ ...props.breakpoint, address: 0 })
      setInstruction(value.length > 0 ? instruction : "")
      return
    }
    // Automatically fill in the instruction if only one match,
    // but only if there wasn't already a filled-in value.
    // That way the user can backspace to delete the current value.
    if (newPopup.length === 1 && instruction.length < 3) {
      value = newPopup[0]
      resetPopup()
    } else {
      setPopup(newPopup)
    }
    setInstruction(value)

    if (newPopup.length <= 1 && value.length === 3) {
      const modes = opTable[value]
      for (let i = 0; i < modes.length; i++) {
        if (modes[i] !== undefined) {
          setAddressMode(addressModes[i])
          props.setBreakpoint({ ...props.breakpoint, address: modes[i] | BRK_INSTR })
          break
        }
      }
    }

    // If we don't have a valid instruction yet, clear out the current value.
    if (value.length < 3 && props.breakpoint) {
      props.setBreakpoint({ ...props.breakpoint, address: BRK_INSTR })
    }
  }

  const handleInstructionFromPopup = (v: string) => {
    handleInstructionChange(popup[parseInt(v ? v : "0", 16)])
  }

  const computeHexvalue = (hexvalue: number, address: number) => {
    if (hexvalue !== -1) {
      const bytes = opCodes[address & 0xFF].bytes
      if (bytes === 1) return -1
      if (bytes === 2) return hexvalue & 0xFF
    }
    return hexvalue
  }

  const handleAddressModeChange = (value: string) => {
    if (instruction.length < 3) return
    const modes = opTable[instruction]
    for (let i = 0; i < modes.length; i++) {
      if (addressModes[i] === value) {
        props.setBreakpoint({ ...props.breakpoint, address: modes[i] | BRK_INSTR })
        setAddressMode(value)
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isModeDisabledForOpcode = (value: string, userdata: number) => {
    if (instruction.length < 3) return true
    const modes = opTable[instruction]
    for (let i = 0; i < modes.length; i++) {
      if (addressModes[i] === value) return (modes[i] ? false : true)
    }
    return true
  }

  const getInstructionBreakpointString = () => {
    let result = ""
    if (props.breakpoint.address >= BRK_INSTR) {
      result = getBreakpointString(props.breakpoint)
    }
    return result ? result : "--"
  }

  const handleHexValueChange = (value: string) => {
    let hexSize = 4
    if (instruction.length === 3) {
      const bytes = opCodes[props.breakpoint.address & 0xFF].bytes
      if (bytes < 3) hexSize = 2
    }
    value = value.replace(/[^0-9a-f]/gi, "").slice(0, hexSize).toUpperCase()
    if (props.breakpoint) {
      props.setBreakpoint({ ...props.breakpoint, hexvalue: parseInt(value ? value : "-1", 16) })
    }
  }

  const handleIllegal65C02Change = (checked: boolean) => {
    setIllegal65C02(checked)
    props.setBreakpoint({ ...props.breakpoint, address: checked ? BRK_ILLEGAL_65C02 : 0 })
  }

  const handleIllegal6502Change = (checked: boolean) => {
    setIllegal6502(checked)
    props.setBreakpoint({ ...props.breakpoint, address: checked ? BRK_ILLEGAL_6502 : 0 })
  }

  const handleMemoryBankChange = (value: string) => {
    for (const key of MemoryBankKeys) {
      const bank = MEMORY_BANKS[key]
      if (bank.name === value) {
        props.setBreakpoint({ ...props.breakpoint, memoryBank: key })
        // bail early since we found a match
        return false
      }
    }
  }

  const v = computeHexvalue(props.breakpoint.hexvalue, props.breakpoint.address)
  const hexvalue = v >= 0 ? v.toString(16).toUpperCase() : ""

  return (
    <div>
      <div className={"flex-row" + ((illegal65C02 || illegal6502) ? " disabled" : "")}
        style={{ alignItems: "baseline" }}>
        <EditField name={t("debug.breakpoint.opcode")}
          initialFocus={true}
          value={instruction}
          setValue={handleInstructionChange}
          placeholder="LDA"
          width="5em" />
        <PullDownMenu values={popup}
          setValue={handleInstructionFromPopup} />
        <Droplist name={t("debug.breakpoint.addressingMode")}
          value={addressMode}
          values={addressModes}
          setValue={handleAddressModeChange}
          userdata={props.breakpoint.address}
          isDisabled={isModeDisabledForOpcode} />
        <EditField name={t("debug.breakpoint.value")}
          value={hexvalue}
          setValue={handleHexValueChange}
          isHex={true}
          placeholder={t("debug.any")}
          width="3em" />
      </div>
      <CheckBox name={t("debug.breakpoint.anyIllegal65C02")}
        checked={props.breakpoint.address === BRK_ILLEGAL_65C02}
        setChecked={handleIllegal65C02Change} />
      <CheckBox name={t("debug.breakpoint.anyIllegal6502")}
        checked={props.breakpoint.address === BRK_ILLEGAL_6502}
        setChecked={handleIllegal6502Change}
        disabled={props.breakpoint.address === BRK_ILLEGAL_65C02} />
      <div className="dialog-title">{getInstructionBreakpointString()}</div>
      <Droplist name={t("debug.breakpoint.memoryBank")}
        value={MEMORY_BANKS[props.breakpoint.memoryBank].name}
        values={MemoryBankNames}
        setValue={handleMemoryBankChange}
        userdata={props.breakpoint.address}
        isDisabled={() => false} />

      <Breakpoint_Actions breakpoint={props.breakpoint} setBreakpoint={props.setBreakpoint}/>

      <Breakpoint_Once breakpoint={props.breakpoint} setBreakpoint={props.setBreakpoint}/>
    </div>
  )
}

export default BPEdit_Instruction
